import { useState } from "react";
import { motion } from "framer-motion";
import { Check, X, ArrowRight } from "lucide-react";
import { QUESTIONS } from "@/lib/quiz";

const EASE = [0.22, 1, 0.36, 1];

const optionClass = (q, i, picked) => {
  const base =
    "flex w-full items-start gap-4 rounded-sm border px-5 py-4 text-left text-base transition-colors duration-200 disabled:cursor-default";
  if (picked === null) return `${base} border-border bg-card hover:border-primary hover:bg-muted/60`;
  if (q.type === "trait") {
    return i === picked
      ? `${base} border-primary bg-primary text-primary-foreground`
      : `${base} border-border bg-card opacity-50`;
  }
  if (q.options[i].correct) return `${base} border-primary bg-primary text-primary-foreground`;
  if (i === picked) return `${base} border-accent bg-accent/10 text-accent`;
  return `${base} border-border bg-card opacity-50`;
};

const QuizQuestion = ({ index, onAnswer }) => {
  const q = QUESTIONS[index];
  const [picked, setPicked] = useState(null);
  const total = QUESTIONS.length;

  const choose = (i) => {
    if (picked !== null) return;
    setPicked(i);
    if (q.type === "trait") setTimeout(() => onAnswer(i), 420);
  };

  const isCorrect = picked !== null && q.type === "knowledge" && q.options[picked].correct;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: EASE }}
      data-testid={`quiz-question-${index}`}
    >
      <div className="flex items-center gap-4">
        <span className="font-display text-2xl italic text-secondary" data-testid="quiz-progress-label">
          {String(index + 1).padStart(2, "0")}
          <span className="text-foreground/40"> / {total}</span>
        </span>
        <div className="h-px flex-1 bg-border">
          <motion.div
            className="h-px bg-primary"
            initial={false}
            animate={{ width: `${((index + 1) / total) * 100}%` }}
            transition={{ duration: 0.6, ease: EASE }}
          />
        </div>
      </div>

      <p className="mt-8 text-xs font-bold uppercase tracking-[0.22em] text-accent">{q.section}</p>
      <h2 className="mt-3 font-display text-3xl font-medium leading-tight tracking-tight text-primary md:text-4xl">
        {q.text}
      </h2>

      <div className="mt-8 grid gap-3">
        {q.options.map((opt, i) => (
          <button
            key={opt.label}
            onClick={() => choose(i)}
            disabled={picked !== null}
            data-testid={`quiz-option-${i}`}
            className={optionClass(q, i, picked)}
          >
            <span className="mt-0.5 font-display text-lg italic opacity-70">₹</span>
            <span className="flex-1">{opt.label}</span>
            {picked !== null && q.type === "knowledge" && opt.correct && <Check size={18} className="mt-1" />}
            {picked === i && q.type === "knowledge" && !opt.correct && <X size={18} className="mt-1" />}
          </button>
        ))}
      </div>

      {picked !== null && q.type === "knowledge" && (
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: EASE }}
          className="mt-6 rounded-sm border border-border bg-muted/60 p-5"
          data-testid="quiz-explain"
        >
          <span
            className={`inline-block rounded-full border px-3 py-1 text-xs font-bold uppercase tracking-[0.18em] ${
              isCorrect ? "border-primary text-primary" : "border-accent text-accent"
            }`}
          >
            {isCorrect ? "Verified" : "Recheck"}
          </span>
          <p className="mt-3 text-sm leading-relaxed text-foreground/80">{q.explain}</p>
          <button
            onClick={() => onAnswer(picked)}
            data-testid="quiz-continue-btn"
            className="group mt-5 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
          >
            Continue
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.div>
      )}
    </motion.div>
  );
};

export default QuizQuestion;
